// ── AllRiskRatingsPage ──
import { useState, useEffect } from "react";
import { B } from "../../../shared/theme";
import { exportCSV, fmtDate, localFilter } from "../../../shared/format";
import { Btn, Card, ErrorMsg, Icon, Pagination, RiskBadge, STh, SearchBar, SectionHeader, Spinner, Td, Th } from "../../../shared/ui";
import { usePagination, useSortable } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";

export const AllRiskRatingsPage = () => {
    const { api, role, view, setModal, setConfirm, delRec } = useApp();
      const [rows,setRows]=useState([]); const [loading,setLoading]=useState(true); const [err,setErr]=useState(null);
      const [q,setQ]=useState("");
      const load=()=>{setLoading(true);api.get("getRiskRatings").then(d=>{setRows(d);setLoading(false);}).catch(e=>{setErr(e.message);setLoading(false);});};
      useEffect(()=>{load();},[]);
      const filtered=localFilter(rows,q,["loannumber","companyname","rating","ratingdate"]);
      const { sorted, sortKey, sortDir, toggle } = useSortable(filtered, 'ratingdate', 'desc');
      const { paginated:displayed, page, setPage, totalPages, total, reset } = usePagination(sorted, 100);

      const latestPerLoan = Object.values(
        sorted.reduce((acc,r) => {
          const existing = acc[r.loanid];
          if (!existing || new Date(r.ratingdate) > new Date(existing.ratingdate)) {
            acc[r.loanid] = r;
          }
          return acc;
        }, {})
      ).filter(r => parseFloat(r.rating));

      const avgRating = latestPerLoan.length > 0
        ? (latestPerLoan.reduce((s,r)=>s+(parseFloat(r.rating)||0),0) / latestPerLoan.length).toFixed(2)
        : null;

      if(loading) return <Spinner />; if(err) return <ErrorMsg msg={err} onRetry={load} />;
      return <>
        <SectionHeader actions={[
          <SearchBar key="s" value={q} onChange={v=>{ setQ(v); reset(); }} placeholder="Search risk ratings..." />,
          <Btn key="ex" onClick={()=>exportCSV(sorted,["loannumber","companyname","ratingdate","rating"],"risk_ratings.csv")}>Export CSV</Btn>
        ]} />
        <Card style={{ overflowX:"auto" }}>
          <table style={{ width:"100%",borderCollapse:"collapse", whiteSpace:"nowrap" }}>
            <thead><tr>
              <STh label="Loan #"      sortKey="loannumber"  currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
              <STh label="Account"     sortKey="companyname" currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
              <STh label="Rating Date" sortKey="ratingdate"  currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
              <STh label="Rating"      sortKey="rating"      currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
              <Th>Portfolio Review</Th>
              <Th></Th>
            </tr></thead>
            <tbody>
              {displayed.map(r=>(
                <tr key={r.id} style={{ cursor:"pointer" }} onClick={()=>setModal({type:"riskrating-view",rec:{...r,id:r.id,loanid:r.loanid}})} onMouseEnter={e=>e.currentTarget.style.background="var(--color-background-secondary)"} onMouseLeave={e=>e.currentTarget.style.background=""}>
                  <Td><span style={{ fontFamily:"var(--font-mono)",fontSize:12 }}>{r.loannumber}</span></Td>
                  <Td style={{ fontWeight:500 }}>{r.companyname}</Td>
                  <Td>{fmtDate(r.ratingdate)}</Td>
                  <Td style={{ textAlign:"right" }}><RiskBadge rating={r.rating} /></Td>
                  <Td onClick={e=>e.stopPropagation()}> 
                    {r.portfolioreviewURL ? <a href={r.portfolioreviewURL} target="_blank" rel="noreferrer" style={{ color:B.cyan }}>Link</a> : "—"} </Td>
                  <Td><div style={{ display:"flex",gap:6, justifyContent:"flex-end" }} onClick={e=>e.stopPropagation()}>
                    <Btn onClick={()=>setModal({type:"riskrating-view",rec:{...r,id:r.id,loanid:r.loanid}})}><Icon type="view" /></Btn>
                    {role.canEdit&&<Btn onClick={()=>setModal({type:"riskrating-edit",rec:{...r,id:r.id,loanid:r.loanid},onDone:load})}><Icon type="edit" /></Btn>}
                    {role.canDelete&&<Btn onClick={()=>setConfirm({msg:"Delete this risk rating?",onConfirm:async()=>{ await delRec("RiskRatings",r.id,"Deleted risk rating",load); setConfirm(null); }})} variant="danger"><Icon type="delete" /></Btn>}
                  </div></Td>
                </tr>
              ))}

              {/* ── Summary row ── */}
          <tr style={{ background:B.cyanLight, borderTop:"2px solid "+B.cyan }}>
            <td colSpan={3} style={{ padding:"10px 12px", fontSize:13, fontWeight:700, color:B.cyanDark, textAlign:"right" }}>
              {sorted.length} rating{sorted.length!==1?"s":""} across {latestPerLoan.length} loan{latestPerLoan.length!==1?"s":""} — Latest Rating Avg
            </td>
            <Td style={{ fontWeight:700, color:B.cyanDark }}>
              {avgRating ? <RiskBadge rating={avgRating} /> : "—"}
            </Td>
            <td colSpan={2} />
          </tr>
            </tbody>
          </table>
          <Pagination page={page} totalPages={totalPages} total={total} pageSize={100} onPage={setPage} />
        </Card>
      </>;
    };
